import secureStorage from '../utils/secureStorage';
// UpdateStatusBadge.js - Status Badges Component for Children Cards
// Shows activity/menu/log update status

import React from 'react';

const UpdateStatusBadge = ({ status, type, lastUpdated, menuCount, menuTotal }) => {
  const getTypeLabel = () => {
    switch (type) {
      case 'activity':
        return 'Aktiviti';
      case 'menu':
        return 'Menu';
      case 'daily_log':
        return 'Log Harian';
      default:
        return type;
    }
  };

  const getTypeColor = () => {
    switch (type) {
      case 'activity':
        return 'blue';
      case 'menu':
        return 'green';
      case 'daily_log':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const color = getTypeColor();

  if (status === 'updated') {
    // For menu type, show the count
    const displayLabel = type === 'menu' && menuCount !== undefined && menuTotal !== undefined
      ? `${getTypeLabel()} ${menuCount}/${menuTotal}`
      : getTypeLabel();
    
    // Determine if menu is fully completed (all meal types recorded)
    const isFullyComplete = type === 'menu' ? menuCount >= menuTotal : true;
    const statusIcon = isFullyComplete ? '✅' : '🔄';
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full bg-${color}-100 border border-${color}-300`}>
        <span className={`font-bold mr-1 ${isFullyComplete ? 'text-green-600' : 'text-orange-500'}`}>{statusIcon}</span>
        <span className={`text-xs font-medium text-${color}-700`}>
          {displayLabel}
        </span>
        {lastUpdated && (
          <span className={`text-xs text-${color}-500 ml-1`}>
            {new Date(lastUpdated).toLocaleTimeString('ms-MY', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        )}
      </div>
    );
  } else {
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full bg-gray-50 border border-gray-200`}>
        <span className="text-orange-500 font-bold mr-1">⚠️</span>
        <span className="text-xs font-medium text-gray-500">
          {type === 'menu' ? `${getTypeLabel()} 0/${menuTotal || 4}` : getTypeLabel()}
        </span>
      </div>
    );
  }
};

// Combined Status Badges for a Child
export const ChildStatusBadges = ({ childId, date, className = "" }) => {
  const [statuses, setStatuses] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const token = secureStorage.getItem('token');
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        const response = await fetch(
          `${apiUrl}/api/children/${childId}/update-status${date ? `?date=${date}` : ''}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setStatuses(data);
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [childId, date]);

  if (loading) {
    return (
      <div className={`flex gap-1 ${className}`}>
        <div className="animate-pulse bg-gray-200 h-6 w-16 rounded-full"></div>
        <div className="animate-pulse bg-gray-200 h-6 w-16 rounded-full"></div>
        <div className="animate-pulse bg-gray-200 h-6 w-16 rounded-full"></div>
      </div>
    );
  }

  if (!statuses) return null;

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      <UpdateStatusBadge
        status={statuses.activity_updated ? 'updated' : 'not_updated'}
        type="activity"
        lastUpdated={statuses.activity_last_updated}
      />
      <UpdateStatusBadge
        status={statuses.menu_updated ? 'updated' : 'not_updated'}
        type="menu"
        lastUpdated={statuses.menu_last_updated}
        menuCount={statuses.menu_count}
        menuTotal={statuses.menu_total}
      />
      <UpdateStatusBadge
        status={statuses.daily_log_updated ? 'updated' : 'not_updated'}
        type="daily_log"
        lastUpdated={statuses.daily_log_last_updated}
      />
    </div>
  );
};

export default UpdateStatusBadge;
